import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Cpu, Building, Zap, Video, Thermometer, Factory, ArrowRight } from 'lucide-react';

const IoTSmartInfra = () => {
  const services = [
    {
      icon: Building,
      title: 'Smart Buildings',
      description: 'IoT-enabled HVAC, lighting, access control, energy management. Intelligent building automation.',
      image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=600&h=400&fit=crop'
    },
    {
      icon: Zap,
      title: 'Smart Utilities',
      description: 'Water, electricity, gas monitoring with predictive maintenance. Real-time utility management.',
      image: 'https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?w=600&h=400&fit=crop'
    },
    {
      icon: Factory,
      title: 'Industrial IoT',
      description: 'Manufacturing automation, predictive maintenance, quality control. Industry 4.0 solutions.',
      image: 'https://images.unsplash.com/photo-1565793298595-6a879b1d9492?w=600&h=400&fit=crop'
    },
    {
      icon: Cpu,
      title: 'Banking & ATM Monitoring',
      description: 'Real-time ATM health, cash forecasting, security monitoring. Connected banking infrastructure.',
      image: 'https://images.unsplash.com/photo-1541746972996-4e0b0f43e02a?w=600&h=400&fit=crop'
    },
    {
      icon: Video,
      title: 'CCTV & Video Analytics',
      description: 'AI-powered video surveillance, face recognition, crowd detection. Intelligent security systems.',
      image: 'https://images.unsplash.com/photo-1557804506-669a67965ba0?w=600&h=400&fit=crop'
    },
    {
      icon: Thermometer,
      title: 'Environmental & Energy Monitoring',
      description: 'Temperature, air quality, energy consumption tracking. Green building compliance.',
      image: 'https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?w=600&h=400&fit=crop'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-indigo-900 via-blue-900 to-cyan-950 text-white py-24 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img 
            src="https://images.unsplash.com/photo-1558346490-a72e53ae2d4f?w=1920&h=800&fit=crop" 
            alt="IoT" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <Cpu className="h-16 md:h-20 w-16 md:w-20 mx-auto mb-6 text-cyan-300 animate-float" />
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">IoT, Smart Infrastructure & Edge AI</h1>
            <p className="text-lg md:text-xl text-cyan-200 mb-8">
              Sensors, edge computing and AI combine to create intelligent, automated physical environments. Connect, monitor, and optimize everything.
            </p>
            <Link to="/contact">
              <Button size="lg" className="bg-white text-indigo-900 hover:bg-cyan-50 text-base md:text-lg px-6 md:px-8 transition-all hover:scale-105">
                Build Smart Infrastructure
              </Button>
            </Link>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="white"/>
          </svg>
        </div>
      </section>

      {/* Services */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">IoT & Smart Infrastructure Solutions</h2>
            <div className="w-24 h-1 bg-indigo-600 mx-auto mb-6"></div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const IconComponent = service.icon;
              return (
                <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 overflow-hidden group">
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={service.image} 
                      alt={service.title} 
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                    <div className="absolute bottom-4 left-4">
                      <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center">
                        <IconComponent className="h-6 w-6 text-indigo-600" />
                      </div>
                    </div>
                  </div>
                  <CardHeader>
                    <CardTitle className="text-xl">{service.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600">{service.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-indigo-900 to-cyan-950 text-white">
        <div className="container mx-auto px-4 md:px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Create Connected Environments</h2>
          <p className="text-lg md:text-xl mb-8 text-cyan-100 max-w-2xl mx-auto">
            Make your infrastructure intelligent with IoT and Edge AI.
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-indigo-900 hover:bg-cyan-50 text-lg px-8 transition-all hover:scale-105">
              Start IoT Project
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default IoTSmartInfra;